/**
 * Compute the tab set the artifact drawer should render for a given
 * artifact, and the default tab to land on when nothing is explicitly
 * selected.
 *
 * analytical variables (`av`, `av_denovo`, `composition`) get the analytical variable tab set. Concept
 * groups get a different tab set, with Definition appearing only when the
 * group has a definition body (rule-based concept groups carry one; flat
 * codesets don't).
 */

import type { EpiArtifact, EpiArtifactTab } from '../../types/epi-chat-types';

// Results was its own tab in the original layout but the surface was
// thin and overlapped Verification (which carries the four epi-section
// payloads — coverage, distribution, concept usage, technical checks —
// derived from the same execution). We collapsed Results into
// Verification for now; the underlying ``EpiArtifact.results`` field
// is still populated by the stream reducer and can be resurfaced as
// a sub-section once we have a clear use case that distinguishes it.
const CONCEPT_GROUP_TABS_WITHOUT_DEFINITION: EpiArtifactTab[] = ['description', 'codes', 'usage'];
const CONCEPT_GROUP_TABS_WITH_DEFINITION: EpiArtifactTab[] = [
  'description',
  'definition',
  'codes',
  'usage',
];

/**
 * Optional capability gates for tabs that aren't on by default for
 * every user. Renderers (``ArtifactDrawer``, ``AvContentCard``) read
 * their Split flags via ``useFeatureFlagSplit`` and pass the booleans
 * in. Defaults are conservative — the tab is hidden when the gate
 * isn't supplied, so a caller that hasn't been updated to read the
 * flag doesn't accidentally surface a gated tab.
 */
export interface TabsForArtifactOptions {
  /**
   * Show the manifest-enriched tabs on analytical variable artifacts (Concepts,
   * Decisions, Recall). Without this gate only Description / SQL /
   * Verification render — those three are the stable surface for
   * every user. Behind the ``av_card_recall`` Split flag.
   *
   * Named after the flag (not the tab) so the field-to-flag link is
   * obvious at the call site even though the gate covers three tabs.
   */
  avCardRecallEnabled?: boolean;
}

/**
 * Split flag name for the analytical variable-card enriched tabs (Concepts / Decisions
 * / Recall). Exported as a constant so the renderers
 * (ArtifactDrawer, AvContentCard) and any test that mocks the
 * flag-eval hook stay in sync on the exact key Split is keyed on —
 * a typo would silently leave the tabs off for everyone.
 */
export const AV_CARD_RECALL_FLAG = 'av_card_recall';

export const tabsForArtifact = (
  artifact: EpiArtifact | null,
  options: TabsForArtifactOptions = {}
): EpiArtifactTab[] => {
  if (!artifact) return [];
  if (artifact.kind === 'concept_group') {
    return artifact.definition
      ? CONCEPT_GROUP_TABS_WITH_DEFINITION
      : CONCEPT_GROUP_TABS_WITHOUT_DEFINITION;
  }
  // av / av_denovo / composition / undefined (legacy) all use the analytical variable shape.
  // Without ``avCardRecallEnabled`` only the stable trio
  // (Description / SQL / Verification) renders — that's the surface
  // every user gets. With the flag on, the manifest-enriched tabs
  // (Concepts / Decisions / Recall) appear when their content exists.
  // Reading flow: what is this → how it's built → which concepts →
  // why those choices → what shaped them → did it work.
  const tabs: EpiArtifactTab[] = ['description', 'sql'];
  if (options.avCardRecallEnabled) {
    if (artifact.conceptBindings && artifact.conceptBindings.length > 0) {
      tabs.push('concepts');
    }
    if (artifact.designDecisions && artifact.designDecisions.length > 0) {
      tabs.push('decisions');
    }
    if (artifact.knowledgeApplied && artifact.knowledgeApplied.length > 0) {
      tabs.push('recall');
    }
  }
  tabs.push('verification');
  return tabs;
};

export const defaultTabForArtifact = (
  artifact: EpiArtifact | null,
  options: TabsForArtifactOptions = {}
): EpiArtifactTab => {
  const tabs = tabsForArtifact(artifact, options);
  return tabs[0] ?? 'description';
};

export const tabLabel = (tab: EpiArtifactTab): string => {
  switch (tab) {
    case 'description':
      return 'Definition';
    case 'sql':
      return 'SQL';
    case 'verification':
      return 'Verification';
    case 'results':
      return 'Results';
    case 'decisions':
      return 'Decisions';
    case 'recall':
      return 'Recall';
    case 'definition':
      return 'Definition';
    case 'codes':
      return 'Codes';
    case 'concepts':
      return 'Concepts';
    case 'usage':
      return 'Usage';
  }
};

/**
 * Resolve the tab the drawer should render given (a) what's been
 * explicitly selected and (b) what the artifact's kind allows. Returns
 * the default tab whenever the requested tab isn't valid for this
 * artifact (e.g. switching from a concept group with Definition to one
 * without should fall back to Description).
 */
export const resolveActiveTab = (
  artifact: EpiArtifact | null,
  requested: EpiArtifactTab | null,
  options: TabsForArtifactOptions = {}
): EpiArtifactTab => {
  const valid = tabsForArtifact(artifact, options);
  if (requested && valid.includes(requested)) return requested;
  return defaultTabForArtifact(artifact, options);
};
